<script setup lang="ts">
  import {
    ref,
    reactive,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    computed,
    h,
    provide
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, Drawer, Modal, message, Spin, Input } from 'ant-design-vue'
  import {
    PlusOutlined,
    CodeOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
  } from '@ant-design/icons-vue'
  import { Plot } from './models/Plot'
  import { PlotSchema } from './models/PlotSchema'
  import PlotSelectionModal from './components/PlotSelectionModal.vue'
  import { Download, FileChartLine, Loader2, Trash2 } from 'lucide-vue-next'
  import PlotConfiguration from './components/PlotConfiguration.vue'
  import { PlottingService } from './services/PlottingService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import PlotJsonManagement from './components/PlotJsonManagement.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { PlottingJsonParsingService } from './services/PlottingJsonParsingService'
  import JsonPlotConfiguration from './components/JsonPlotConfiguration.vue'
  import { PlottingJsonPlotConfigurationService } from './services/PlottingJsonPlotConfigurationService'
  import { PlottingTypeGuardService } from './services/PlottingTypeGuardService'
  import { PlottingSearchService } from './services/PlotSearchService'
  import { IPlotsApiService, PlotsApiService } from './services/PlotApiService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { PlotsUiService } from './services/PlotsUiService'
  import { PlotSchemaUiFieldGroup } from './models/PlotSchemaUi'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { toast } from 'vue-sonner'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import {
    GetExecutionOutputFileArgs,
    GetExecutionOutputFileType
  } from '@/shared/Executions/models/GetExecutionOutputFileArgs'
  import { useUser } from '@/composables/useUser'
  import { GetAplosFileArgs } from '../FileManager/models/GetAplosFileArgs'
  import {
    AplosFileApiService,
    IAplosFileApiService
  } from '../FileManager/services/AplosFileApiService'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import ExecutionSelectionTable from '../ExecutionHistory/components/ExecutionSelectionTable.vue'
  import { PlotDataSourceType } from './models/PlotDataSourceType'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import {
    ExecutionPhaseService,
    IExecutionPhaseService
  } from '@/shared/Executions/services/ExecutionPhaseService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'

  interface IPlottingProps {
    plots?: Array<Plot>
    postProcessingExecution?: Execution
    analysisExecution?: Execution
    customCalculationExecution?: Execution
    analysisFile?: AplosFile
    cleanedFile?: AplosFile
    pkResultsFile?: AplosFile
    pkResultsCustomCalcsFile?: AplosFile
    wizardModel?: AnalysisWizardModel
    analysisSelectionDisabled?: boolean // in the wizard this component should rely on an input analysisExecution
    showJsonManagement?: boolean
  }

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )
  const plotsApiService: IPlotsApiService = InjectionService.injectStrict(
    PlotsApiService.injectableKey
  )

  const plottingJsonParsingService: PlottingJsonParsingService = InjectionService.injectStrict(
    PlottingJsonParsingService.injectableKey
  )

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const executionPhaseService: IExecutionPhaseService = InjectionService.injectStrict(
    ExecutionPhaseService.injectableKey
  )

  const aplosFileApiService: IAplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<IPlottingProps>()

  const emit = defineEmits<{
    (e: 'update:plots', value: Array<Plot>): void
    (e: 'update:postProcessingExecution', value?: Execution): void
    (e: 'update:pkResultsFile', value?: AplosFile): void
    (e: 'update:pkResultsCustomCalcsFile', value?: AplosFile): void
    (e: 'update:showJsonManagement', value: boolean): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const executionName = ref<string>(props.postProcessingExecution?.name ?? '')
  const execution = ref<Execution | undefined>(props.postProcessingExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  const isExecuted = computed(() => execution.value?.id != null)

  watch(
    () => props.postProcessingExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  watch(
    () => executionName.value,
    (val) => {
      // invalidate existing execution if previously executed and use new instance to store name
      if (execution.value?.id || !execution.value) {
        execution.value = { name: val } as Execution
      } else if (execution.value) {
        execution.value.name = val
      }
      emit('update:postProcessingExecution', execution.value)
    },
    { immediate: false }
  )

  const auth = useUser()

  const isDownloadingResults = ref<boolean>(false)
  const isLoadingPkResultsFile = ref<boolean>(false)
  const selectedPkResultsFile = ref<AplosFile | undefined>(props.pkResultsFile)
  const isLoadingPkResultsCustomCalcsFile = ref<boolean>(false)
  const selectedPkResultsCustomCalcsFile = ref<AplosFile | undefined>(
    props.pkResultsCustomCalcsFile
  )
  const isLoadingAnalysisFile = ref<boolean>(false)
  const selectedAnalysisFile = ref<AplosFile | undefined>(props.analysisFile)
  const selectedCleanedFile = ref<AplosFile | undefined>(props.cleanedFile)

  const isLoadingAvailablePlots = ref<boolean>(false)
  const hasErrorLoadingPlots = ref<boolean>(false)
  const availablePlotSchemas = ref<PlotSchema[]>([])
  const activePlots = reactive<Plot[]>([])
  const selectedPlot = ref<Plot | null>(null)
  const showPlotSelection = ref(false)

  const showAnalysisSelectionModal = ref(false)
  const selectedAnalysisExecutionId = ref<string>()
  const selectedCustomCalculationExecutionId = ref<string>()

  const parentExecutionId = computed(
    () => selectedCustomCalculationExecutionId.value ?? selectedAnalysisExecutionId.value
  )

  const showJsonManagement = ref(props.showJsonManagement)

  watch(
    () => props.showJsonManagement,
    (val) => {
      showJsonManagement.value = !!val
    }
  )

  watch(showJsonManagement, (val) => {
    emit('update:showJsonManagement', !!val)
  })

  const availableColumns = ref<Array<{ value: string; label: string }>>([])
  const availableParams = ref<Array<{ value: string; label: string }>>([])

  const availableDataSources = computed(() => {
    const sources: Array<{ id: string; value: string; label: string; type: PlotDataSourceType }> =
      []

    if (selectedAnalysisFile?.value) {
      if (!selectedAnalysisFile.value?.name)
        throw Error(
          'Encountered selectedAnalysisFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        id: selectedAnalysisFile.value?.id!,
        value: selectedAnalysisFile.value?.name,
        label: selectedAnalysisFile.value?.name!,
        type: PlotDataSourceType.AnalysisInput
      })
    }

    if (selectedCleanedFile?.value) {
      if (!selectedCleanedFile.value?.name)
        throw Error(
          'Encountered selectedCleanedFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        id: selectedCleanedFile.value?.id!,
        value: selectedCleanedFile.value?.name,
        label: selectedCleanedFile.value?.name,
        type: PlotDataSourceType.CleanedData
      })
    }

    if (selectedPkResultsFile?.value) {
      if (!selectedPkResultsFile.value?.name)
        throw Error(
          'Encountered selectedPkResultsFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        id: selectedPkResultsFile.value?.id!,
        value: selectedPkResultsFile.value?.name,
        label: selectedPkResultsFile.value?.name,
        type: PlotDataSourceType.PkResults
      })
    }

    if (selectedPkResultsCustomCalcsFile?.value) {
      if (!selectedPkResultsCustomCalcsFile.value?.name)
        throw Error(
          'Encountered selectedPkResultsCustomCalcsFile with missing name when trying to populate availableDataSources!'
        )

      sources.push({
        id: selectedPkResultsCustomCalcsFile.value?.id!,
        value: selectedPkResultsCustomCalcsFile.value?.name,
        label: selectedPkResultsCustomCalcsFile.value?.name,
        type: PlotDataSourceType.PkResultsCustomCalcs
      })
    }

    return sources
  })

  const loadAvailableColumnsAndParamCodes = (inputFile?: AplosFile) => {
    availableColumns.value = []
    availableParams.value = []

    let columnNames = inputFile?.metadata?.columnNames

    if (columnNames?.length) {
      // sort alphanumerically (natural numeric-aware ordering) then map
      const sortedCols = [...columnNames].sort((a, b) =>
        String(a)
          .trim()
          .localeCompare(String(b).trim(), undefined, { numeric: true, sensitivity: 'base' })
      )
      availableColumns.value = sortedCols.map((name) => ({ value: name, label: name }))
    }

    let paramCodes = inputFile?.metadata?.paramList
    if (paramCodes?.length) {
      // sort param list by paramcd alphanumerically then map
      const sortedParams = [...paramCodes].sort((a, b) =>
        String(a.paramcd).trim().localeCompare(String(b.paramcd).trim(), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      )
      availableParams.value = sortedParams.map((param) => ({
        value: param.paramcd,
        label: `${param.paramcd} (${param.param})`
      }))
    }
  }

  const setPkResultsFileForAnalysisExecution = async (
    analysisExecution: Execution | string
  ): Promise<AplosFile> => {
    const analysisExecutionId =
      typeof analysisExecution === 'string' ? analysisExecution : analysisExecution?.id

    if (!analysisExecutionId) {
      toast.error('A valid Analysis Execution is required to load PK Results File for Plots.')
    }

    try {
      isLoadingPkResultsFile.value = true
      await auth.initialize()
      const args = new GetExecutionOutputFileArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: analysisExecutionId!,
        fileType: GetExecutionOutputFileType.PkResults
      })

      const pkResultsFileReponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        executionApiService.getExecutionOutput.bind(executionApiService),
        args
      )

      if (!pkResultsFileReponse || !pkResultsFileReponse.data) {
        toast.error('No PK Results file was returned for the given Analysis Execution.')
        throw new Error('PK Results file response is empty')
      }
      emit('update:pkResultsFile', pkResultsFileReponse.data)
      selectedPkResultsFile.value = pkResultsFileReponse.data
      return pkResultsFileReponse.data
    } catch (error) {
      toast.error('An error was encountered loading PK Results File for Plots.')
      throw error
    } finally {
      isLoadingPkResultsFile.value = false
    }
  }

  const setAnalysisFile = async (analysisFileId: string): Promise<AplosFile> => {
    try {
      isLoadingAnalysisFile.value = true
      await auth.initialize()

      const args = new GetAplosFileArgs({
        tenantId: auth.user.tenant_id as string,
        userId: auth.user.id as string,
        fileId: analysisFileId
      })

      const analysisFileResponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        aplosFileApiService.getFile.bind(aplosFileApiService),
        args
      )

      if (!analysisFileResponse || !analysisFileResponse.data) {
        toast.error('No Analysis file was returned for the given Analysis Execution.')
        throw new Error('PK Analysis file response is empty')
      }
      selectedAnalysisFile.value = analysisFileResponse.data
      return analysisFileResponse.data
    } catch (error) {
      toast.error('An error was encountered loading Analysis File for Plots.')
      throw error
    } finally {
      isLoadingAnalysisFile.value = false
    }
  }

  const setPkResultsCustomCalcsFileForCustomCalculationExecution = async (
    customcalculationsExecution: Execution | string
  ): Promise<AplosFile> => {
    const customCalculationExecutionId =
      typeof customcalculationsExecution === 'string'
        ? customcalculationsExecution
        : customcalculationsExecution?.id

    if (!customCalculationExecutionId) {
      toast.error(
        'A valid Custom Calculation Execution is required to load PK Results Custom Calcs File for Plots.'
      )
    }

    try {
      isLoadingPkResultsCustomCalcsFile.value = true
      await auth.initialize()
      const args = new GetExecutionOutputFileArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: customCalculationExecutionId!,
        fileType: GetExecutionOutputFileType.PkResultsCustomCalcs
      })

      const pkResultsCustomCalcsFileReponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        executionApiService.getExecutionOutput.bind(executionApiService),
        args
      )

      if (!pkResultsCustomCalcsFileReponse || !pkResultsCustomCalcsFileReponse.data) {
        toast.error(
          'No PK Results Custom Calcs file was returned for the given Custom Calculation Execution.'
        )
        throw new Error('PK Results Custom Calcs file response is empty')
      }

      selectedPkResultsCustomCalcsFile.value = pkResultsCustomCalcsFileReponse.data
      emit('update:pkResultsCustomCalcsFile', pkResultsCustomCalcsFileReponse.data)
      return pkResultsCustomCalcsFileReponse.data
    } catch (error) {
      toast.error('An error was encountered loading PK Results Custom Calcs File for Plots.')
      throw error
    } finally {
      isLoadingPkResultsCustomCalcsFile.value = false
    }
  }

  const isProcessingPlots = ref<boolean>(false)
  const draggedPlot = ref<Plot | null>(null)
  const highlightedPlotId = ref<string | null>(null)
  const deletingPlotId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activePlotTabKey = ref<string>('view')
  const activePlotViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activePlotEditConfiguration = ref<{} | null>(null)

  watch(
    selectedPlot,
    (newVal) => {
      if (newVal) {
        const flattenedPlots = PlottingService.getFlattendPlots([newVal])
        activePlotEditConfiguration.value = flattenedPlots?.length ? flattenedPlots[0] : null
      }
    },
    { deep: false }
  )

  // Ensure last updated (UI) rule configuration is flattened and update
  // Json editor model ref
  const onPlottingViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedPlot.value == null || activePlotEditConfiguration.value == null) return
    if (activeMode === 'edit') {
      const flattenedPlots = PlottingService.getFlattendPlots([selectedPlot.value])
      activePlotEditConfiguration.value = flattenedPlots?.length ? flattenedPlots[0] : null
    }
  }

  const loadAvailablePlotSchemas = async () => {
    hasErrorLoadingPlots.value = false
    isLoadingAvailablePlots.value = true
    try {
      const plotSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.PlotSchema,
        customSchemaStore
      )

      availablePlotSchemas.value = plotSchemas
    } catch (error) {
      hasErrorLoadingPlots.value = true
      message.error('Failed to load available plots.')
    } finally {
      isLoadingAvailablePlots.value = false
    }
  }

  // --- Add a new plot from schema ---
  const addPlot = (plotSchema: PlotSchema): Plot => {
    const newPlot = PlottingService.createPlotFromPlotSchema(plotSchema)
    activePlots.push(newPlot)
    selectedPlot.value = newPlot
    updateSharedModelOnPlotChange()
    message.success(`Added plot: ${plotSchema.display_name}`)
    return newPlot
  }

  const selectPlot = (plot: Plot) => {
    selectedPlot.value = plot
  }

  const removePlotTimeoutMs: number = 600
  const removePlot = (plot: Plot) => {
    // Start deletion animation
    animatePlotDeletion(plot.id as string)
    message.success(`Removed "${plot.display_name}"`)
  }

  const animatePlotDeletion = (plotId: string) => {
    deletingPlotId.value = plotId
    setTimeout(() => {
      const idx = activePlots.findIndex((p) => p.id === plotId)
      if (idx > -1) activePlots.splice(idx, 1)
      if (selectedPlot.value?.id === plotId) {
        if (activePlots?.length > 0) {
          selectedPlot.value = activePlots[0]
        } else {
          selectedPlot.value = null
        }
      }
      deletingPlotId.value = null
      updateSharedModelOnPlotChange()
    }, removePlotTimeoutMs)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Handle file selection from Execution History
  const handleAnalysisExecutionSelected = async (
    analysisExecution: Execution,
    customCalculationExecution?: Execution | string
  ) => {
    showAnalysisSelectionModal.value = false
    const primaryAnalysisExecutionPhase =
      executionPhaseService.tryGetPrimaryPhaseTypeForExecution(analysisExecution)
    if (primaryAnalysisExecutionPhase !== ExecutionPhaseType.NCAAnalysis) {
      toast.error('Only NCA Analysis executions may be used in configuring plots!')
      //return
    }

    if (customCalculationExecution != null && typeof customCalculationExecution !== 'string') {
      const primaryCustomCalculationExecutionPhase =
        executionPhaseService.tryGetPrimaryPhaseTypeForExecution(customCalculationExecution)
      if (primaryCustomCalculationExecutionPhase !== ExecutionPhaseType.CustomCalculations) {
        toast.error(
          'Only a secondary Custom Calculation execution may be used in configuring plots!'
        )
        //return
      }

      selectedCustomCalculationExecutionId.value = customCalculationExecution?.id
    }

    if (!selectedAnalysisFile && !analysisExecution?.input?.fileId) {
      toast.error(
        'No input analysisFile was supplied and selected execution does not contain an input file!'
      )
      return
    }

    selectedAnalysisExecutionId.value = analysisExecution?.id

    const promises: Array<Promise<void>> = []

    if (!selectedPkResultsFile.value) {
      promises.push(
        (async () => {
          await setPkResultsFileForAnalysisExecution(analysisExecution)
        })()
      )
    }

    if (!selectedAnalysisFile.value) {
      promises.push(
        (async () => {
          await setAnalysisFile(analysisExecution.input?.fileId!)
        })()
      )
    }

    if (customCalculationExecution != null && !selectedPkResultsCustomCalcsFile.value) {
      promises.push(
        (async () => {
          await setPkResultsCustomCalcsFileForCustomCalculationExecution(customCalculationExecution)
        })()
      )
    }

    await Promise.all(promises)

    //TODO preset a data source or rely on user input?
    return
  }

  const clearAnalysisSelection = () => {
    selectedAnalysisExecutionId.value = undefined
    selectedPkResultsFile.value = undefined
    selectedPkResultsCustomCalcsFile.value = undefined
    selectedAnalysisFile.value = undefined
    availableColumns.value = []
    availableParams.value = []

    if (activePlots?.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Clear existing plots?',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          activePlots.splice(0, activePlots.length)
          selectedPlot.value = null
        },
        onCancel() {}
      })
    }
  }

  // Initialize flatRules or load for cleanedFileId and
  const initializeFlatPlots = (flatPlots?: Array<Plot>) => {
    try {
      isLoadingAvailablePlots.value = true
      const initializedPlots: Array<Plot> = []
      let inputPlots = flatPlots || []
      inputPlots.forEach((flatPlot: Plot) => {
        const initializedPlot = plottingJsonParsingService.initializeEntity(
          flatPlot,
          availablePlotSchemas.value,
          1
        )
        initializedPlots.push(initializedPlot)
      })
      // Replace the contents of the reactive array so Vue picks up the change
      activePlots.splice(0, activePlots.length, ...initializedPlots)
      // Set the first rule as active
      if (activePlots.length > 0) selectedPlot.value = activePlots[0]
    } catch {
      message.error('Failed to initialize plots.')
    } finally {
      isLoadingAvailablePlots.value = false
    }
  }

  watch(
    () => props.analysisExecution,
    async (analysisExecution?: Execution) => {
      if (analysisExecution) {
        // for wizard integration we rely on calcs execution from shared model
        handleAnalysisExecutionSelected(analysisExecution, props.customCalculationExecution)
      }
    },
    { immediate: true }
  )

  const onSubmitPlotsForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Re-executing Plots will reset any Post Processing Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitPlotsForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitPlotsForGeneration()
    }
  }

  const submitPlotsForGeneration = async () => {
    isProcessingPlots.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const flattendPlots = PlottingService.getFlattendPlots(activePlots)

      const args = PlottingService.createQueuePlotsArgs(
        selectedAnalysisExecutionId.value as string,
        flattendPlots,
        executionName.value
      )

      emit('update:plots', args.plots)

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Generating Plots',
        props.wizardModel,
        plotsApiService.processPlots.bind(plotsApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processPlotsResponse = response?.data as QueueExecutionResponse
      if (!processPlotsResponse?.executionId) throw Error('An unexpected error has occurred!')
      const successMessage = 'Generating Plots'
      /* processRulesResponse?.message?.length
          //? processRulesResponse.message
          : 'Plots queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processPlotsResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:postProcessingExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingPlots.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered generating plots:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
    isProcessingPlots.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered generating plots')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Generating Plots')
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingPlots.value ||
      execution.value?.id == null ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const movePlotUp = (plot: Plot) => {
    const currentIndex = activePlots.findIndex((p) => p.id === plot.id)
    if (currentIndex > 0) {
      const temp = activePlots[currentIndex]
      activePlots[currentIndex] = activePlots[currentIndex - 1]
      activePlots[currentIndex - 1] = temp
      highlightMovedPlot(plot.id as string)
      message.success(`Moved "${plot.display_name}" up to position ${currentIndex}`)
    }
  }

  const movePlotDown = (plot: Plot) => {
    const currentIndex = activePlots.findIndex((p) => p.id === plot.id)
    if (currentIndex < activePlots.length - 1) {
      const temp = activePlots[currentIndex]
      activePlots[currentIndex] = activePlots[currentIndex + 1]
      activePlots[currentIndex + 1] = temp
      highlightMovedPlot(plot.id as string)
      message.success(`Moved "${plot.display_name}" down to position ${currentIndex + 2}`)
    }
  }

  const highlightMovedPlot = (plotId: string) => {
    highlightedPlotId.value = plotId
    setTimeout(() => {
      highlightedPlotId.value = null
    }, 1500)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, plot: Plot) => {
    draggedPlot.value = plot
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', plot.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    if (draggedPlot.value) {
      const draggedIndex = activePlots.findIndex((p) => p.id === draggedPlot.value!.id)
      if (draggedIndex !== targetIndex) {
        const [movedPlot] = activePlots.splice(draggedIndex, 1)
        activePlots.splice(targetIndex, 0, movedPlot)
        highlightMovedPlot(draggedPlot.value.id as string)
        message.success(`Moved "${draggedPlot.value.display_name}" to position ${targetIndex + 1}`)
      }
    }
    draggedPlot.value = null
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    draggedPlot.value = null
    dragOverIndex.value = -1
  }

  // Raw Json Edits are parsed back through rule initialization process
  // similarly to how we import json files
  const handleJsonEditorConfigurationChange = () => {
    if (selectedPlot.value != null && activePlotEditConfiguration.value != null) {
      const editConfigurationJson = JSON.stringify([activePlotEditConfiguration.value])
      const result = plottingJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availablePlotSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingPlotIndex = activePlots.findIndex((x) => x.id === selectedPlot.value?.id)
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingPlotIndex <= -1
        )
          throw new Error('Error updating plots from Json Rule Editor value!')
        const updatedPlot = (result.parsedEntities as Array<Plot>)[0]
        const existingPlot = activePlots[existingPlotIndex]
        Object.assign(existingPlot, { ...updatedPlot, id: existingPlot.id })
        selectedPlot.value = existingPlot
      }
    }
  }

  // Update available columns and paramcds for selected well-known datasource type
  const handleDataSourceSelected = (value: PlotDataSourceType) => {
    if (!value) return

    switch (value) {
      case PlotDataSourceType.AnalysisInput:
        loadAvailableColumnsAndParamCodes(selectedAnalysisFile?.value)
        break
      case PlotDataSourceType.CleanedData:
        loadAvailableColumnsAndParamCodes(selectedCleanedFile?.value)
        break
      case PlotDataSourceType.PkResults:
        loadAvailableColumnsAndParamCodes(selectedPkResultsFile?.value)
        break
      case PlotDataSourceType.PkResultsCustomCalcs:
        loadAvailableColumnsAndParamCodes(selectedPkResultsCustomCalcsFile?.value)
        break
    }
  }

  const shouldClearPostProcessingExecution = computed(() => isExecuted.value && !isExecuting.value)

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnPlotChange = () => {
    const postProcessingExecution = shouldClearPostProcessingExecution.value
      ? undefined
      : execution.value
    emit('update:postProcessingExecution', postProcessingExecution)
    const flattenedPlots = PlottingService.getFlattendPlots(activePlots)
    emit('update:plots', flattenedPlots)
  }

  // Handle rule configuration updates
  const handleConfigurationUpdate = async (
    plot: Plot,
    config: Record<string, any>,
    plotTypeChange?: boolean
  ) => {
    const plotSearchResults = PlottingSearchService.findPlotByIdRecursive(
      activePlots,
      plot.id as string
    )
    if (plotSearchResults != null) {
      const topLevelParentRuleIndex = activePlots.findIndex(
        (p) => p.id === plotSearchResults.topLevelParent?.id
      )
      if (topLevelParentRuleIndex > -1) {
        // For plotTypeChange we initialize new Plot instance and
        // either splice top-level activePlots. Nested plots are currently not supported
        if (plotTypeChange === true) {
          const newPlotSchema = PlottingService.tryGetPlotSchemaByType(
            plot._type,
            availablePlotSchemas.value
          )
          if (newPlotSchema == null)
            throw new Error(`Error getting PlotSchema for plot.type: ${plot.type}!`)
          selectedPlot.value = null
          // Create new plot from schema resetting any existing metadata
          let newPlot = PlottingService.createPlotFromPlotSchema(newPlotSchema)
          // Initialize any sub-plot metadata if neccessary
          newPlot.description = plot.configuration?.description
          newPlot.configuration = { ...config }
          if (
            plotSearchResults.topLevelParent != null &&
            plotSearchResults.topLevelParent !== plotSearchResults.plot &&
            plotSearchResults.path?.length > 1
          ) {
            // Recursive search to find and update the view model
            const relativePath = plotSearchResults.path.slice(1)
            PlottingSearchService.setPlotAtPath(
              plotSearchResults.topLevelParent,
              relativePath,
              newPlot
            )
          } else {
            activePlots.splice(topLevelParentRuleIndex, 1, newPlot)
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedPlot.value = activePlots[topLevelParentRuleIndex]
          })
        } else if (plotSearchResults.topLevelParent === plotSearchResults.plot) {
          // No new rule changes, just commit updated configuration
          activePlots[topLevelParentRuleIndex].configuration = { ...config }
          activePlots[topLevelParentRuleIndex]._type = config._type
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnPlotChange()
      }
    }
  }

  const onJsonManagementPlotsApplied = () => {
    selectedPlot.value = activePlots?.length ? activePlots[0] : null
    updateSharedModelOnPlotChange()
  }

  // Add uiFieldGroups computed property
  const uiFieldGroups = computed(() => {
    return PlotsUiService.getPlotSchemaUiFieldGroups(selectedPlot.value?.schema)
  })

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (selectedPlot.value == null || selectedPlot.value._type === PlottingService.CUSTOM_PLOT_TYPE)
      return true
    const activeSchemaType = PlottingService.tryGetPlotSchemaByType(
      selectedPlot.value._type as string,
      availablePlotSchemas.value
    )
    return activeSchemaType == null || activeSchemaType.type === PlottingService.CUSTOM_PLOT_TYPE
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activePlotViewMode.value = { mode: 'edit' }
      } else if (activePlotViewMode.value.mode !== 'edit') {
        activePlotViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.postProcessingExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.postProcessingExecution?.id == null
      ) {
        stepStatusMessage = 'Plots not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailablePlotSchemas()

    updateWizardStepStatus()

    // Watch input flat rules and ensure initialized (after schemas loaded)
    initializeFlatPlots(props.plots)

    watch(
      activePlots,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // Ensure first tab is selected on update of selected plot
  // persist JSON tab selection if already active
  watch(
    [selectedPlot, uiFieldGroups],
    ([plot, groups]) => {
      if (plot != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activePlotTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (!groups.some((g: PlotSchemaUiFieldGroup) => g.name === activePlotTabKey.value)) {
          activePlotTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  // --- Add this watcher to reset tab selection on selectedPlot change ---
  watch(selectedPlot, (newPlot, oldPlot) => {
    if (newPlot && newPlot !== oldPlot) {
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activePlotTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        activePlotViewMode.value = { mode: 'view' }
      } else {
        activePlotViewMode.value = { mode: 'edit' }
      }
    }
  })

  const { isExtraLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activePlots,
    addPlot,
    removePlot
  })
</script>

<template>
  <div
    :class="[
      'plotting-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'h-full': isFitToBrowser,
        'plotting-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter :class="isFitToBrowser ? 'h-full' : ''">
      <SplitterPanel :size="30" style="min-width: 350px">
        <div :class="['flex', 'flex-col', 'border-r-2', 'shadow-sm', 'h-full']">
          <!-- Analysis Selection UI -->
          <div class="border-b p-4 shadow-sm">
            <div class="mb-3" v-if="!analysisSelectionDisabled">
              <label class="mb-1 block text-sm font-medium">Source Analysis</label>
              <div class="mb-2 flex items-center gap-2">
                <!-- Select Analysis -->
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  :disabled="
                    isLoadingPkResultsFile ||
                    isLoadingPkResultsCustomCalcsFile ||
                    isLoadingAnalysisFile ||
                    isLoadingAvailablePlots
                  "
                  block
                  @click="showAnalysisSelectionModal = true"
                  :title="
                    selectedAnalysisExecutionId ? 'Change selected Anaylsis' : 'Select an Analysis'
                  "
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedAnalysisExecutionId ?? 'Select Analysis' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="selectedAnalysisExecutionId && !isLoadingPkResultsFile"
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearAnalysisSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>

            <!-- Post Processing Wizard Plots/Tables component select -->
            <slot v-if="wizardModel" name="wizardContentSelect" />

            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Name</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Execution Name' }}
                </template>
                <Input
                  v-model:value="executionName"
                  size="large"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    isLoadingAvailablePlots ||
                    isLoadingAnalysisFile ||
                    isLoadingPkResultsFile ||
                    isLoadingPkResultsCustomCalcsFile
                  "
                  placeholder="Execution Name"
                />
              </a-tooltip>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Plots</h3>
                <p class="mt-1 text-sm">
                  {{ activePlots.length }}
                  {{ activePlots.length === 1 ? 'plot' : 'plots' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Plot' }}
                  </template>
                  <Button
                    :disabled="
                      selectedAnalysisExecutionId == null ||
                      isLoadingAvailablePlots ||
                      isLoadingAnalysisFile ||
                      isLoadingPkResultsFile ||
                      isLoadingPkResultsCustomCalcsFile
                    "
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showPlotSelection = true"
                    title="Add New Rule"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagement = true"
                    title="View/Export JSON"
                    :disabled="
                      (selectedAnalysisExecutionId == null ||
                        isLoadingAvailablePlots ||
                        isLoadingAnalysisFile ||
                        isLoadingPkResultsFile ||
                        isLoadingPkResultsCustomCalcsFile) &&
                      (activePlots.length === 0 || isProcessingPlots)
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="plots-list-container flex-1 p-4">
            <!-- Plots List -->
            <div class="plots-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <!-- Empty State -->
              <div
                v-if="activePlots.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Plots Yet</h3>
                <!-- Quick Tips -->
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                    <li>• Plots are created and configured after selecting a file</li>
                    <li>• Use the + button to add a plot type</li>
                    <li>• Configure each plot in the right panel</li>
                  </ul>
                </div>
              </div>
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(plot, index) in activePlots"
                  :key="plot.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedPlot?.id === plot.id,
                      'opacity-50': draggedPlot?.id === plot.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'rule-moved-highlight': highlightedPlotId === plot.id,
                      'rule-deleting': deletingPlotId === plot.id
                    }
                  ]"
                  :bodyStyle="{
                    padding: '18px'
                  }"
                  draggable="true"
                  @click="selectPlot(plot)"
                  @dragstart="handleDragStart($event, plot)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <!-- Drag Handle -->
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab text-gray-400 active:cursor-grabbing" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ plot.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ plot.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>
                      <!-- Plot Controls -->
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Button
                          type="text"
                          size="small"
                          :disabled="activePlots.findIndex((p) => p.id === plot.id) === 0"
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="movePlotUp(plot)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activePlots.findIndex((p) => p.id === plot.id) ===
                            activePlots.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="movePlotDown(plot)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removePlot(plot)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedPlot
                    ? `Configure: ${selectedPlot.display_name}`
                    : 'Plotting Configuration'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedAnalysisExecutionId != null
                    ? selectedPlot != null
                      ? 'Configure the selected plot parameters below'
                      : 'Select a plot from the left panel to configure its settings'
                    : 'Select an analysis to begin configuring plots'
                }}
              </p>
            </div>
            <slot v-if="wizardModel" name="wizardNavigation" />

            <div v-else class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    !execution?.id || !activePlots?.length
                      ? 'Generate Plots to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <a-tooltip
                placement="left"
                :open="
                  selectedAnalysisExecutionId == null || !activePlots?.length || isProcessingPlots
                    ? false
                    : undefined
                "
              >
                <template #title>
                  {{
                    selectedAnalysisExecutionId == null || !activePlots?.length
                      ? 'Select an analysis and add plots to Generate Plots.'
                      : !isProcessingPlots
                        ? 'Generate Plots'
                        : 'Generating Plots'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedAnalysisExecutionId == null || !activePlots?.length || isProcessingPlots
                  "
                  @click="onSubmitPlotsForGenerationClicked"
                >
                  <Loader2 v-if="isProcessingPlots" class="size-4 animate-spin" />
                  <FileChartLine v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedAnalysisExecutionId == null || !activePlots?.length || isProcessingPlots
                  "
                  @click="onSubmitPlotsForGenerationClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingPlots" class="size-4 animate-spin" />
                    <FileChartLine v-else class="h-4 w-4" />
                    {{ 'Generate Plots' }}
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>
          <div
            v-if="
              (isLoadingAvailablePlots ||
                isLoadingAnalysisFile ||
                isLoadingPkResultsFile ||
                isLoadingPkResultsCustomCalcsFile) &&
              !wizardModel
            "
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="selectedAnalysisExecutionId == null && !hasErrorLoadingPlots"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select an analysis to begin configuring plots
            </p>
            <div v-else-if="hasErrorLoadingPlots" class="mt-8 flex justify-center text-red-400">
              <WarningFilled class="mr-2 text-2xl" />
              An error was encountered loading plots.
            </div>

            <!-- Dynamic UI Field Groups Tabs (like Calculations.vue) -->
            <div
              v-else-if="selectedPlot != null && uiFieldGroups != null && uiFieldGroups?.length"
              class="min-h-0 flex-1"
            >
              <a-tabs
                :key="selectedPlot?.id"
                v-model:activeKey="activePlotTabKey"
                @change="onPlottingViewModeTabChange"
                :destroyInactiveTabPane="true"
              >
                <a-tab-pane
                  v-for="(fieldGroup, index) in uiFieldGroups"
                  :key="fieldGroup.name"
                  :tab="fieldGroup.name"
                >
                  <PlotConfiguration
                    :configuration-level="1"
                    :plot="selectedPlot"
                    :ui-field-group="fieldGroup"
                    :available-columns="availableColumns"
                    :available-params="availableParams"
                    :available-data-sources="availableDataSources"
                    @configuration-updated="handleConfigurationUpdate"
                    @data-source-selected="handleDataSourceSelected"
                    :plot-schemas="availablePlotSchemas"
                    :show-description="index === 0"
                    :is-condensed-layout="true"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    PlottingTypeGuardService.isPayloadTemplatePlotSchema(selectedPlot.schema)
                  "
                >
                  <div class="mt-[20px]">
                    <JsonPlotConfiguration
                      v-if="activePlotEditConfiguration != null"
                      :key="selectedPlot?.id"
                      :plot-schemas="availablePlotSchemas"
                      :available-columns="availableColumns"
                      v-model="activePlotEditConfiguration"
                      :editor-schema="
                        PlottingJsonPlotConfigurationService.getJsonEditorSchemaForPlot()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>

            <!-- Fallback: Old two-tab mode if no uiFieldGroups -->
            <a-tabs
              v-else-if="selectedPlot != null"
              @change="onPlottingViewModeTabChange"
              :key="selectedPlot?.id"
              v-model:activeKey="activePlotViewMode.mode"
              :destroyInactiveTabPane="true"
            >
              <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                <PlotConfiguration
                  :configuration-level="1"
                  :plot="selectedPlot"
                  :available-columns="availableColumns"
                  :available-params="availableParams"
                  :available-data-sources="availableDataSources"
                  @configuration-updated="handleConfigurationUpdate"
                  @data-source-selected="handleDataSourceSelected"
                  :plot-schemas="availablePlotSchemas"
                  :show-description="true"
                  :is-condensed-layout="true"
                />
              </a-tab-pane>
              <a-tab-pane
                key="edit"
                tab="JSON"
                :disabled="
                  PlottingTypeGuardService.isPayloadTemplatePlotSchema(selectedPlot.schema)
                "
              >
                <div class="mt-[20px]">
                  <JsonPlotConfiguration
                    v-if="activePlotEditConfiguration != null"
                    :key="selectedPlot?.id"
                    :plot-schemas="availablePlotSchemas"
                    :available-columns="availableColumns"
                    v-model="activePlotEditConfiguration"
                    :editor-schema="
                      PlottingJsonPlotConfigurationService.getJsonEditorSchemaForPlot()
                    "
                    :place-holder="'Configure JSON'"
                    :configuration-level="1"
                    @update:modelValue="handleJsonEditorConfigurationChange"
                    :allow-type-change="false"
                  />
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <!-- Plot Selection Modal -->
    <PlotSelectionModal
      v-model:visible="showPlotSelection"
      :plot-schemas="availablePlotSchemas"
      @plot-selected="addPlot"
    />

    <!-- Analysis Selection Modal -->
    <Modal
      v-model:open="showAnalysisSelectionModal"
      title="Select an Analysis"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <ExecutionSelectionTable
          @execution-selected="handleAnalysisExecutionSelected"
          :execution-type-filter="[ExecutionPhaseType.NCAAnalysis]"
        />
      </div>
    </Modal>

    <!-- JSON Export Drawer -->
    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Plotting JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <PlotJsonManagement
        v-if="parentExecutionId != null && availablePlotSchemas != null"
        :key="selectedPlot?.id"
        :plot-schemas="availablePlotSchemas"
        :parent-execution-id="parentExecutionId"
        v-model:execution-name="executionName"
        :plots="activePlots"
        :visibility-changed="showJsonManagement"
        :plotting-json-parsing-service="plottingJsonParsingService"
        @plots-applied="onJsonManagementPlotsApplied"
      />
    </Drawer>
  </div>
</template>

<style scoped>
  /* make the button a flexible item and allow it to shrink in a flex row */
  .file-select-button {
    text-align: center;
    flex: 1 1 0; /* take remaining space but allow shrinking */
    min-width: 0; /* CRITICAL: allow shrinking so ellipsis can take effect */
  }

  /* Ensure the Ant button inner wrapper behaves as a constrained block */
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block; /* give the element a width */
    width: 100%; /* constrain to button width */
    overflow: hidden; /* required for ellipsis */
    white-space: nowrap; /* required for ellipsis */
    text-overflow: ellipsis; /* show "..." when truncated */
  }

  /* Summary: adjust layout CSS so component doesn't expand parent height and left list becomes the single vertical scroller. */
  .plotting-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  /* apply flex fill only when layout mode requires filling the browser viewport */
  .plotting-fill {
    flex: 1 1 0;
  }

  /* left list container becomes a flex column and will handle scrolling itself */
  .plots-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  /* full-height behavior only when fit-to-browser */
  .fit-to-browser .plots-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }
  .plots-scroll-area {
    min-height: 0;
    overflow: visible;
  }

  .selected-plot {
    border: 2px solid #1890ff;
  }
  .drop-zone-active {
    height: 4px;
    background-color: rgba(37, 99, 235, 0.2);
    transition: height 0.2s ease;
  }
  .plot-moved-highlight {
    animation: plotMovedPulse 1.5s ease-in-out;
    border: 2px solid #10b981 !important;
    background-color: #ecfdf5 !important;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1) !important;
  }
  @keyframes plotMovedPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }
  .plot-deleting {
    animation: plotDeletePulse 0.6s ease-in-out;
    border: 2px solid #ef4444 !important;
    background-color: #fef2f2 !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }
  @keyframes plotDeletePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    25% {
      transform: scale(1.02);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.3);
    }
    75% {
      transform: scale(0.98);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      opacity: 0;
    }
  }

  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
